import { ApiResult } from '@ftr/foundation'
import { ClassConstructor } from '@ftr/serialization'
import { lastValueFrom } from 'rxjs'
import { isErrorConflict } from './is-error-conflict'

export interface APIConflictConfiguration<TConflictResponse, TSuccessResponse> {
  responseConstructor: ClassConstructor<TConflictResponse>
  request: (conflictToken: string | undefined) => ApiResult<TSuccessResponse>
  onSuccess: (data: TSuccessResponse) => Promise<void>
  onError: (error: Error) => void
  onCancel?: () => void
  onConflict: (responseBody: TConflictResponse, hasPreviousConflictResolutionTokenExpired: boolean) => Promise<boolean>
}

export async function apiRequestWithConflict<T extends { conflictResolutionToken: string }, Data>(
  config: APIConflictConfiguration<T, Data>,
): Promise<void> {
  let hasPreviousConflictResolutionTokenExpired = false
  let currentConflictToken: string | undefined = undefined

  const handleFailure = async (error: Error): Promise<void> => {
    if (isErrorConflict(error)) {
      const body = error.getConflictBody(config.responseConstructor)

      hasPreviousConflictResolutionTokenExpired =
        currentConflictToken !== undefined && currentConflictToken !== body.conflictResolutionToken

      currentConflictToken = body.conflictResolutionToken

      const conflictAccepted = await config.onConflict(body, hasPreviousConflictResolutionTokenExpired)

      return conflictAccepted ? doRequest() : config.onCancel?.()
    } else {
      return config.onError(error)
    }
  }

  const doRequest = async (): Promise<void> => {
    const result = await lastValueFrom(config.request(currentConflictToken))
    if (result.isFailure()) return handleFailure(result._error)
    else if (result.isSuccess()) return config.onSuccess(result._data)
  }

  return doRequest()
}
